import {
  MutationCache,
  QueryCache,
  QueryClient,
  isServer
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api/client';
import type { ApiResponse } from '@/lib/api/types';
import { STALE_TIME } from '@/lib/query/stale-times';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      skipGlobalSuccessToast?: boolean;
      skipGlobalErrorToast?: boolean;
      operationType?: 'create' | 'update' | 'delete';
      fallbackSuccessMessage?: string;
    };
  }
}

function getErrorMessage(error: Error): string {
  if (error instanceof ApiError) {
    // 422 validation: show only the first field error
    if (error.status === 422 && error.data) {
      const errorData = error.data as {
        errors?: Record<string, string[]>;
      };
      if (errorData.errors && typeof errorData.errors === 'object') {
        const firstField = Object.values(errorData.errors)[0];
        if (Array.isArray(firstField) && firstField.length > 0) {
          return firstField[0];
        }
      }
    }

    if (error.message) return error.message;
    return `Request failed (${error.status})`;
  }

  if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
    const lang =
      typeof document !== 'undefined' ? document.documentElement.lang : 'en';
    return lang === 'ar'
      ? 'خطأ في الشبكة. يرجى التحقق من اتصالك.'
      : 'Network error. Please check your connection.';
  }

  if (error.message === 'Request timeout') {
    const lang =
      typeof document !== 'undefined' ? document.documentElement.lang : 'en';
    return lang === 'ar'
      ? 'انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.'
      : 'Request timed out. Please try again.';
  }

  return error.message || 'An unexpected error occurred.';
}

type MutationMeta = {
  skipGlobalSuccessToast?: boolean;
  skipGlobalErrorToast?: boolean;
  operationType?: 'create' | 'update' | 'delete';
  fallbackSuccessMessage?: string;
};

function showSuccessToast(data: unknown, mutation: { meta?: MutationMeta }) {
  if (mutation.meta?.skipGlobalSuccessToast) return;

  const message =
    (data as ApiResponse)?.message || mutation.meta?.fallbackSuccessMessage;
  if (!message) return;

  switch (mutation.meta?.operationType) {
    case 'create':
      toast.success(message);
      break;
    case 'update':
      toast.info(message);
      break;
    case 'delete':
      toast.warning(message);
      break;
    default:
      toast.success(message);
  }
}

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof ApiError && error.status === 401) return;
        // License expiry redirects to the license-expired page — no toast spam.
        if (error instanceof ApiError && error.code === 'LICENSE_EXPIRED')
          return;
        toast.error(getErrorMessage(error));
      }
    }),
    mutationCache: new MutationCache({
      onSuccess: (data, _variables, _context, mutation) => {
        showSuccessToast(data, mutation);
      },
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.skipGlobalErrorToast) return;
        if (error instanceof ApiError && error.status === 401) return;
        if (error instanceof ApiError && error.code === 'LICENSE_EXPIRED')
          return;
        toast.error(getErrorMessage(error));
      }
    }),
    defaultOptions: {
      queries: {
        // Was 0, which — combined with the framework's default
        // `refetchOnMount: true` — refetched every query on every mount. Going
        // back to /dashboard refired all 19 metric endpoints every time.
        //
        // `refetchOnMount` is deliberately left at its default: it means
        // "refetch on mount IF stale", which is the behaviour we want now that
        // staleTime is non-zero. Setting it to false would mean "never refetch
        // on mount even when stale", which is a different and much worse thing.
        //
        // Safe only for data whose changes originate in this app:
        // `invalidateQueries` marks a query stale regardless of staleTime, so a
        // mutation still shows the user their own write. Data the backend
        // advances on its own — queue jobs moving a pipeline stage, another
        // operator's scanning session — has no mutation to invalidate it and
        // must opt into `STALE_TIME.live` or a `refetchInterval`.
        staleTime: STALE_TIME.list,
        gcTime: 5 * 60 * 1000,
        retry: (failureCount, error) => {
          if (
            error instanceof ApiError &&
            error.status >= 400 &&
            error.status < 500
          ) {
            return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false
      },
      mutations: {
        retry: false
      }
    }
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  }
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
}
